/**
 * Notices that belong to one query result, rendered once above its grid:
 *
 *  - Seam notice (product UI, DESIGN §10d): only when the result reads more
 *    than one source AND a requested item has a seam. A single season or an
 *    explicit single source never gets one; detail charts never do.
 *  - Pinned-source line (product UI, decision DQ): the query names a source, so
 *    every season reads from it. The one place a provider is named outside dev.
 *  - Recoded line: saved filter values whose meaning moved (`changed`) or that
 *    no longer match anything (`unmapped`). Aliases and id translations keep
 *    their meaning and stay silent (F1 contract §7a, decision DP).
 *  - Dev source label: the resolved sources, behind `FeatureFlags.sourceLabel`.
 */
import { useEffect, useMemo, useState } from "react";
import { Box, ButtonBase, IconButton, Stack, Typography } from "@mui/material";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import SyncAltOutlined from "@mui/icons-material/SyncAltOutlined";
import PushPinOutlined from "@mui/icons-material/PushPinOutlined";
import Close from "@mui/icons-material/Close";
import ExpandMore from "@mui/icons-material/ExpandMore";
import ExpandLess from "@mui/icons-material/ExpandLess";
import type {
  ExplorerSource,
  QueryResolution,
  RecodedValue,
  SeamsCrossed,
} from "../../../models/Explorer";
import type { ExplorerAPI } from "../../../util/Requests";
import { useSeamCatalog, SeamCatalog } from "../../../hooks/useSeamCatalog";
import DevSourceLabel from "../DevSourceLabel";
import { pinnedSourceName } from "../PinnedSource/PinnedSource";
import { FeatureFlags } from "../../../util/featureFlags";

// ============================================================================
// Pure helpers (tested directly)
// ============================================================================

/** The §10d rule: more than one resolved source and at least one seam entry. */
export function shouldShowSeamNotice(
  seamsCrossed: SeamsCrossed | undefined,
  resolution: QueryResolution | undefined,
): boolean {
  if (!seamsCrossed || Object.keys(seamsCrossed).length === 0) return false;
  // The backend only emits seams for multi-source queries; this guards a stale
  // or cached response against the rule rather than trusting it blindly.
  if (resolution && (resolution.sources?.length ?? 0) < 2) return false;
  return true;
}

export interface SeamGroup {
  seam: string;
  title: string;
  summary: string | null;
  items: string[];
}

/** "rim_rule" → "Rim rule": the fallback title while the seams route is missing. */
export function humanizeSeamId(seam: string): string {
  const text = seam.replace(/_/g, " ").trim();
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const byName = (a: string, b: string) =>
  a.localeCompare(b, undefined, { sensitivity: "base" });

/** One entry per seam, with the result's columns that cross it (catalog order-free, stable). */
export function groupSeams(
  seamsCrossed: SeamsCrossed,
  catalog: SeamCatalog | null | undefined,
): SeamGroup[] {
  const bySeam = new Map<string, string[]>();
  for (const [item, seams] of Object.entries(seamsCrossed)) {
    for (const seam of seams) {
      const items = bySeam.get(seam) ?? [];
      if (!items.includes(item)) items.push(item);
      bySeam.set(seam, items);
    }
  }
  return Array.from(bySeam.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([seam, items]) => {
      const info = catalog?.[seam];
      return {
        seam,
        title: info?.title || humanizeSeamId(seam),
        summary: info?.summary || null,
        items: items.sort(byName),
      };
    });
}

/** "2023-24" from a start year. */
export function formatSeasonLabel(startYear: number): string {
  return `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;
}

/**
 * The season the definitions change at: the catalog's `firstSeasonCtg`, else
 * the first season of the later source in the result's resolution.
 */
export function seamSeason(
  groups: SeamGroup[],
  catalog: SeamCatalog | null | undefined,
  resolution: QueryResolution | undefined,
): number | null {
  const fromCatalog = groups
    .map((g) => catalog?.[g.seam]?.firstSeasonCtg)
    .filter((y): y is number => typeof y === "number");
  if (fromCatalog.length) return Math.min(...fromCatalog);
  const firsts = Object.values(resolution?.seasonsBySource ?? {})
    .filter((ys): ys is number[] => Array.isArray(ys) && ys.length > 0)
    .map((ys) => Math.min(...ys))
    .sort((a, b) => a - b);
  return firsts.length > 1 ? firsts[1] : null;
}

/** The recodes worth telling a user about: the meaning moved, or the value is gone. */
export function visibleRecodes(
  recoded: RecodedValue[] | undefined,
): RecodedValue[] {
  return (recoded ?? []).filter(
    (r) => r.kind === "changed" || r.kind === "unmapped",
  );
}

export function recodedHeadline(recoded: RecodedValue[]): string {
  const changed = recoded.filter((r) => r.kind === "changed").length;
  const gone = recoded.filter((r) => r.kind === "unmapped").length;
  const parts: string[] = [];
  if (changed) {
    parts.push(
      `${changed} saved filter value${changed === 1 ? "" : "s"} changed meaning`,
    );
  }
  if (gone) {
    const noun = changed
      ? `${gone}`
      : `${gone} saved filter value${gone === 1 ? "" : "s"}`;
    parts.push(`${noun} no longer ${gone === 1 ? "exists" : "exist"}`);
  }
  return parts.join("; ");
}

function recodeDetail(r: RecodedValue): string {
  if (r.kind === "unmapped") {
    return `${r.filter}: saved value “${r.from}” no longer matches anything`;
  }
  const to = r.to.map((v) => `“${v}”`).join(", ");
  return `${r.filter}: “${r.from}” now reads as ${to}`;
}

// ============================================================================
// Components
// ============================================================================

const barSx = (accent: string, background: string) => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 1,
  px: 1.25,
  py: 0.5,
  borderLeft: `3px solid ${accent}`,
  backgroundColor: background,
  fontSize: "0.78rem",
  lineHeight: 1.45,
  color: "rgba(0, 0, 0, 0.78)",
});

const toggleSx = {
  fontSize: "0.72rem",
  fontWeight: 600,
  color: "rgba(0, 0, 0, 0.6)",
  display: "inline-flex",
  alignItems: "center",
  ml: 0.75,
  borderRadius: "3px",
  px: 0.25,
  "&:hover": { color: "rgba(0, 0, 0, 0.87)" },
};

export function SeamNotice({
  seamsCrossed,
  resolution,
  catalog,
}: {
  seamsCrossed: SeamsCrossed;
  resolution?: QueryResolution;
  catalog: SeamCatalog | null | undefined;
}) {
  const [open, setOpen] = useState(false);
  const groups = useMemo(
    () => groupSeams(seamsCrossed, catalog),
    [seamsCrossed, catalog],
  );
  const columns = Object.keys(seamsCrossed).sort(byName);
  const season = seamSeason(groups, catalog, resolution);

  return (
    <Box sx={barSx("#5b7a99", "#f3f6f9")} role="note" data-testid="seam-notice">
      <SyncAltOutlined sx={{ fontSize: "1rem", mt: "2px", color: "#5b7a99" }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box component="span" sx={{ fontWeight: 700 }}>
          {season
            ? `Definitions change at ${formatSeasonLabel(season)}`
            : "Definitions change inside this season range"}
        </Box>
        {" — "}
        {columns.length === 1 ? "1 column" : `${columns.length} columns`} in this
        result {columns.length === 1 ? "compares" : "compare"} across it:{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          {columns.join(", ")}
        </Box>
        <ButtonBase
          sx={toggleSx}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? "Hide" : "Details"}
          {open ? (
            <ExpandLess sx={{ fontSize: "0.95rem" }} />
          ) : (
            <ExpandMore sx={{ fontSize: "0.95rem" }} />
          )}
        </ButtonBase>
        {open && (
          <Stack component="ul" spacing={0.5} sx={{ m: 0, mt: 0.5, pl: 2 }}>
            {groups.map((g) => (
              <li key={g.seam} data-testid="seam-notice-item">
                <Box component="span" sx={{ fontWeight: 700 }}>
                  {g.title}
                </Box>
                <Box component="span" sx={{ color: "rgba(0,0,0,0.6)" }}>
                  {" "}
                  ({g.items.join(", ")})
                </Box>
                {g.summary && (
                  <Typography
                    component="div"
                    sx={{ fontSize: "0.74rem", color: "rgba(0,0,0,0.7)" }}
                  >
                    {g.summary}
                  </Typography>
                )}
              </li>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
}

/** One neutral line under a pinned result: which source every season reads. */
export function PinnedSourceLine({
  source,
}: {
  source: ExplorerSource | null | undefined;
}) {
  const name = pinnedSourceName(source);
  if (!name) return null;
  return (
    <Box
      sx={barSx("#9e9e9e", "#f5f5f5")}
      role="note"
      data-testid="pinned-source-line"
    >
      <PushPinOutlined sx={{ fontSize: "1rem", mt: "2px", color: "#757575" }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Box component="span" sx={{ fontWeight: 700 }}>
          Pinned to {name}
        </Box>
        {" — every season in this query reads from this data source."}
      </Box>
    </Box>
  );
}

export function RecodedLine({ recoded }: { recoded: RecodedValue[] }) {
  const [open, setOpen] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  // A new result brings its own list; a dismissal belongs to the old one.
  useEffect(() => {
    setDismissed(false);
    setOpen(false);
  }, [recoded]);

  if (dismissed || recoded.length === 0) return null;
  return (
    <Box sx={barSx("#c98a1b", "#fcf7ee")} role="status" data-testid="recoded-line">
      <InfoOutlined sx={{ fontSize: "1rem", mt: "2px", color: "#b0771a" }} />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {recodedHeadline(recoded)}
        <ButtonBase
          sx={toggleSx}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? "Hide" : "Show"}
          {open ? (
            <ExpandLess sx={{ fontSize: "0.95rem" }} />
          ) : (
            <ExpandMore sx={{ fontSize: "0.95rem" }} />
          )}
        </ButtonBase>
        {open && (
          <Box component="ul" sx={{ m: 0, mt: 0.25, pl: 2 }}>
            {recoded.map((r, i) => (
              <li key={`${r.filter}-${r.from}-${i}`}>{recodeDetail(r)}</li>
            ))}
          </Box>
        )}
      </Box>
      <IconButton
        size="small"
        aria-label="Dismiss"
        onClick={() => setDismissed(true)}
        sx={{ p: 0.25, mt: "-1px" }}
      >
        <Close sx={{ fontSize: "0.95rem" }} />
      </IconButton>
    </Box>
  );
}

export default function QueryResultNotices({
  api,
  seamsCrossed,
  recoded,
  resolution,
  pinnedSource,
}: {
  api: ExplorerAPI;
  seamsCrossed?: SeamsCrossed;
  recoded?: RecodedValue[];
  resolution?: QueryResolution;
  /** exec_query `pinnedSource` */
  pinnedSource?: ExplorerSource | null;
}) {
  const showSeams = shouldShowSeamNotice(seamsCrossed, resolution);
  const catalog = useSeamCatalog(api, showSeams);
  const shownRecodes = useMemo(() => visibleRecodes(recoded), [recoded]);
  const showPinned = pinnedSourceName(pinnedSource) !== null;
  const showDev = FeatureFlags.sourceLabel && !!resolution?.sources?.length;

  if (!showSeams && !showPinned && shownRecodes.length === 0 && !showDev)
    return null;
  return (
    <Stack spacing={0.5} sx={{ mb: 0.75, flexShrink: 0 }}>
      {showPinned && <PinnedSourceLine source={pinnedSource} />}
      {showSeams && (
        <SeamNotice
          seamsCrossed={seamsCrossed!}
          resolution={resolution}
          catalog={catalog}
        />
      )}
      {shownRecodes.length > 0 && <RecodedLine recoded={shownRecodes} />}
      {showDev && (
        <Box title={resolution?.notes?.join("\n")}>
          <DevSourceLabel resolution={resolution} />
          {resolution?.notes?.length ? (
            <Box
              component="span"
              sx={{ ml: 1, fontSize: "0.7rem", color: "#757575" }}
            >
              {resolution.notes.join(" · ")}
            </Box>
          ) : null}
        </Box>
      )}
    </Stack>
  );
}
